import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FaArrowLeft,
    FaCheckCircle,
    FaTimesCircle,
    FaClock,
    FaUserClock,
    FaMapMarkerAlt,
    FaEye,
} from "react-icons/fa";
import { sendRequest } from "../../../utils/api";
import { formatTime } from "../../../utils/date";

const AttendanceStatus = () => {
    const navigate = useNavigate();
    const { status } = useParams();

    const allowStatus = ["present", "absent"];

    const [attendanceList, setAttendanceList] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAttendance = async () => {
            try {
                setLoading(true);

                const {
                    attendanceList, totalAbsentee, totalAttendee
                } = await sendRequest("GET", `/attendance/history?status=${status}&date=today`);

                const attendances = status === "present"
                    ? attendanceList : attendanceList.map((att: any) => ({
                        user: {
                            ...att,
                        },
                        site: att?.assignSite[0]?.site
                    }));


                setAttendanceList(attendances);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchAttendance();
    }, []);

    if (!status || !allowStatus.includes(status)) navigate("/404")

    const getStatusIcon = () => {
        switch (status) {
            case "present":
                return FaCheckCircle({});

            case "absent":
                return FaTimesCircle({});

            case "late":
                return FaClock({});

            default:
                return FaUserClock({});
        }
    };

    const getStatusStyle = () => {
        switch (status) {
            case "present":
                return "bg-green-50 text-green-600";

            case "absent":
                return "bg-red-50 text-red-600";

            case "late":
                return "bg-yellow-50 text-yellow-600";

            default:
                return "bg-indigo-50 text-indigo-600";
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 px-4 py-6 sm:px-6 lg:px-8">

            <div className="mx-auto max-w-7xl">

                {/* Back */}
                <button
                    onClick={() => navigate(-1)}
                    className="mb-6 flex items-center gap-2 text-sm font-medium text-gray-600 transition hover:text-indigo-600"
                >
                    {FaArrowLeft({ size: 13 })}
                    Back to Attendance
                </button>

                {/* Header */}
                <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">

                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                        <div className="flex items-center gap-4">

                            <div
                                className={`flex h-12 w-12 items-center justify-center rounded-xl text-lg ${getStatusStyle()}`}
                            >
                                {getStatusIcon()}
                            </div>

                            <div>
                                <h1 className="text-xl font-bold text-gray-900 sm:text-2xl capitalize">
                                    {status} Staff
                                </h1>

                                <p className="mt-1 text-sm text-gray-500">
                                    Staff attendance records marked as{" "}
                                    {status}.
                                </p>
                            </div>

                        </div>

                        <div className="rounded-lg bg-gray-50 px-4 py-2 text-sm text-gray-600">
                            {attendanceList.length} Staff
                        </div>

                    </div>

                </div>

                {/* Loading */}
                {loading && (
                    <div className="flex min-h-[300px] items-center justify-center rounded-2xl border border-gray-200 bg-white">
                        <div className="h-9 w-9 animate-spin rounded-full border-4 border-gray-200 border-t-indigo-600" />
                    </div>
                )}

                {/* Empty */}
                {!loading && attendanceList.length === 0 && (
                    <div className="rounded-2xl border border-gray-200 bg-white px-5 py-16 text-center">

                        <div
                            className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full text-xl ${getStatusStyle()}`}
                        >
                            {getStatusIcon()}
                        </div>

                        <h2 className="mt-4 text-lg font-semibold text-gray-900">
                            No {status} records
                        </h2>

                        <p className="mt-1 text-sm text-gray-500">
                            There are currently no staff records under this
                            category.
                        </p>

                    </div>
                )}

                {/* Desktop Table */}
                {!loading && attendanceList.length > 0 && (
                    <div className="hidden overflow-hidden rounded-2xl border border-gray-200 bg-white md:block">

                        <div className="overflow-x-auto">

                            <table className="min-w-full">

                                <thead className="bg-gray-50">

                                    <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">

                                        <th className="px-6 py-4">
                                            Staff
                                        </th>

                                        <th className="px-6 py-4">
                                            Department
                                        </th>

                                        <th className="px-6 py-4">
                                            Site
                                        </th>

                                        <th className="px-6 py-4">
                                            Clock In
                                        </th>

                                        <th className="px-6 py-4">
                                            Clock Out
                                        </th>

                                        {/* <th className="px-6 py-4">
                                            Status
                                        </th> */}

                                        {status === "present" && <th className="px-6 py-4 text-center">
                                            Action
                                        </th>}

                                    </tr>

                                </thead>

                                <tbody className="divide-y divide-gray-100">

                                    {attendanceList.map((staff) => (

                                        <tr
                                            key={staff.id}
                                            className="transition hover:bg-gray-50"
                                        >

                                            <td className="px-6 py-4">

                                                <div className="flex items-center gap-3">

                                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                                                        {`${staff?.user?.firstName} ${staff?.user?.lastName}`
                                                            ?.split(" ")
                                                            .map((n: string) => n[0])
                                                            .join("")
                                                            .slice(0, 2)}
                                                    </div>

                                                    <div>
                                                        <p className="font-semibold text-gray-900">
                                                            {`${staff?.user?.firstName} ${staff?.user?.lastName}`}
                                                        </p>

                                                        <p className="text-xs text-gray-400">
                                                            {staff?.user?.staffId}
                                                        </p>
                                                    </div>

                                                </div>

                                            </td>

                                            <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                                                {staff?.user?.portfolio}
                                            </td>

                                            <td className="px-6 py-4 text-sm capitalize text-gray-600">
                                                {staff?.site?.name}
                                            </td>

                                            <td className="px-6 py-4 text-sm text-gray-700">
                                                {staff.clockIn ? formatTime(staff.clockIn) : "---"}
                                            </td>

                                            <td className="px-6 py-4 text-sm text-gray-700">
                                                {staff.clockOut ? formatTime(staff.clockOut) : "---"}
                                            </td>

                                            {/* <td className="px-6 py-4">

                                                <span
                                                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${getStatusStyle()}`}
                                                >
                                                    {getStatusIcon()}
                                                    {status}
                                                </span>

                                            </td> */}

                                            {status === "present" && <td className="px-6 py-4 text-center">

                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/attendances/${staff.id}`
                                                        )
                                                    }
                                                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
                                                >
                                                    {FaEye({})}
                                                    View
                                                </button>

                                            </td>}

                                        </tr>

                                    ))}

                                </tbody>

                            </table>

                        </div>

                    </div>
                )}

                {/* Mobile Cards */}
                {!loading && attendanceList.length > 0 && (
                    <div className="space-y-4 md:hidden">

                        {attendanceList.map((staff) => (

                            <div
                                key={staff.id}
                                className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                            >

                                <div className="flex items-center gap-3 border-b border-gray-100 p-4">

                                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600">
                                        {`${staff?.user?.firstName} ${staff?.user?.lastName}`
                                            ?.split(" ")
                                            .map((n: string) => n[0])
                                            .join("")
                                            .slice(0, 2)}
                                    </div>

                                 
                                    <div className="min-w-0">
                                        <h3 className="truncate font-semibold text-gray-900">
                                          {`${staff?.user?.firstName} ${staff?.user?.lastName}`}
                                        </h3>

                                        <p className="text-xs text-gray-400">
                                            {staff.user?.staffId}
                                        </p>
                                    </div>

                                </div>

                                <div className="grid grid-cols-2 gap-4 p-4">

                                    <div>
                                        <p className="text-xs text-gray-400">
                                            Department
                                        </p>

                                        <p className="mt-1 truncate text-sm font-medium text-gray-800 capitalize">
                                            {staff?.user.portfolio}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-xs text-gray-400">
                                            Site
                                        </p>

                                        <p className="mt-1 truncate text-sm font-medium capitalize text-gray-800">
                                            {staff.site?.name}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-xs text-gray-400">
                                            Clock In
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-gray-800">
                                            {staff.clockIn ? formatTime(staff.clockIn) : "---"}
                                        </p>
                                    </div>

                                    <div>
                                        <p className="text-xs text-gray-400">
                                            Clock Out
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-gray-800">
                                            {staff.clockOut ? formatTime(staff.clockOut) : "---"}
                                        </p>
                                    </div>

                                </div>

                                {status === "present" && <div className="border-t border-gray-100 p-4">

                                    <button
                                        onClick={() =>
                                            navigate(
                                                `/attendances/${staff.id}`
                                            )
                                        }
                                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3 text-sm font-medium text-white transition hover:bg-indigo-700"
                                    >
                                        {FaEye({})}
                                        View Attendance
                                    </button>

                                </div>}

                            </div>

                        ))}

                    </div>
                )}

            </div>

        </div>
    );
};

export default AttendanceStatus;